import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, Plus, CheckCircle2, Circle, Edit, Trash2, Clock, ListFilter, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API, norm } from '@/lib/helpers';
import { formatBrazilDateTimeCompact } from '@/lib/timezone';

const FILTROS = [
  { key: 'todas', label: 'Todas', color: 'bg-purple-100 text-purple-600' },
  { key: 'pendentes', label: 'Pendentes', color: 'bg-amber-100 text-amber-600' },
  { key: 'respondidas', label: 'Respondidas', color: 'bg-green-100 text-green-600' },
];

export default function PerguntasTab({ perguntas, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState('');
  const [filter, setFilter] = useState('todas');
  const [search, setSearch] = useState('');

  const counts = {
    todas: (perguntas || []).length,
    pendentes: (perguntas || []).filter(p => !p.respondida).length,
    respondidas: (perguntas || []).filter(p => p.respondida).length,
  };

  const filtered = (perguntas || []).filter(p => {
    if (filter === 'respondidas' && !p.respondida) return false;
    if (filter === 'pendentes' && p.respondida) return false;
    if (search && !norm(p.pergunta).includes(norm(search))) return false;
    return true;
  });

  const handleSave = async () => {
    if (!form.trim()) { toast.error('Escreva a pergunta'); return; }
    try {
      const url = editItem ? `${API}/perguntas/${editItem.pergunta_id}` : `${API}/perguntas`;
      const method = editItem ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ pergunta: form.trim() }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(editItem ? 'Pergunta atualizada' : 'Pergunta criada');
      setShowAdd(false); setEditItem(null); setForm(''); onRefresh();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async (p) => {
    try {
      const r = await fetch(`${API}/perguntas/${p.pergunta_id}/toggle`, { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error('Erro');
      onRefresh();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const handleDelete = async (p) => {
    try {
      const r = await fetch(`${API}/perguntas/${p.pergunta_id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('Erro');
      toast.success('Pergunta excluida'); onRefresh();
    } catch { toast.error('Erro ao excluir'); }
  };

  const openEdit = (p) => { setEditItem(p); setForm(p.pergunta); setShowAdd(true); };
  const openAdd = () => { setEditItem(null); setForm(''); setShowAdd(true); };

  const fmtDate = (d) => {
    return formatBrazilDateTimeCompact(d, d || '');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Perguntas</h2>
                  <Badge className="bg-purple-100 text-purple-700 text-sm font-bold">{counts.todas}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="perguntas-menu">
              {FILTROS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filtro-${f.key}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-purple-600 bg-purple-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.color.split(' ')[0]}`}>
                      <ListFilter className={`w-4 h-4 ${f.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-purple-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={openAdd} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="add-pergunta-btn">
                <Plus className="w-4 h-4 mr-1" />Nova Pergunta
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'pergunta' : 'perguntas'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="perguntas-search" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="perguntas-list">
              {filtered.map(p => (
                <div key={p.pergunta_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`pergunta-${p.pergunta_id}`}>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <button onClick={() => handleToggle(p)} className="mt-0.5 flex-shrink-0" data-testid={`toggle-pergunta-${p.pergunta_id}`}>
                      {p.respondida
                        ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                        : <Circle className="w-5 h-5 text-slate-300 hover:text-green-400 transition-colors" />
                      }
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900">{p.pergunta}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />{fmtDate(p.created_at)}
                        </span>
                        <Badge className={`text-xs ${p.respondida ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {p.respondida ? 'Respondida' : 'Pendente'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(p)} data-testid={`edit-pergunta-${p.pergunta_id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => handleDelete(p)} data-testid={`delete-pergunta-${p.pergunta_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma pergunta {filter !== 'todas' ? filter.slice(0, -1) : ''}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAdd} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditItem(null); setForm(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editItem ? 'Editar Pergunta' : 'Nova Pergunta'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Textarea value={form} onChange={e => setForm(e.target.value)} rows={4} maxLength={9000} data-testid="input-pergunta" autoFocus className="resize-none" />
            <p className="text-xs text-slate-400 text-right">{form.length}/9000</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancelar</Button>
              <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700" data-testid="save-pergunta-btn">Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
